'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import type { BlogListPost } from '@/components/blog/blog-list-helpers'

type BlogListResponse = {
  hasNextPage?: boolean
  nextPage?: number | null
  posts?: BlogListPost[]
}

type BlogListClientProps = {
  categoryIds: string[]
  hasNextPage: boolean
  initialPosts: BlogListPost[]
  limit: number
  loadingMessage?: string
  nextPage?: number | null
  showDescription?: boolean
}

function BlogListCard({
  post,
  showDescription,
}: {
  post: BlogListPost
  showDescription?: boolean
}) {
  const description = showDescription ? post.description?.trim() : null
  const heightClass = description ? 'min-h-[10rem]' : 'h-[10rem]'

  return (
    <article className="theme-card overflow-hidden border p-0">
      <a
        href={post.href}
        className={`grid ${heightClass} transition-opacity hover:opacity-90 ${
          post.featuredImage ? 'grid-cols-[minmax(0,10rem)_1fr]' : ''
        }`}
      >
        {post.featuredImage && (
          <div className="relative h-full w-full border-r border-theme-card-border">
            <Image
              src={post.featuredImage.url}
              alt={post.featuredImage.alt}
              fill
              sizes="(max-width: 768px) 100vw, 10rem"
              className="object-cover"
            />
          </div>
        )}
        <div className="flex flex-col justify-center gap-1 px-4 py-5 md:px-6">
          <p className="theme-card-title">{post.title}</p>
          {post.publishDate && <p className="theme-card-content">{post.publishDate}</p>}
          {description && <p className="theme-card-content">{description}</p>}
        </div>
      </a>
    </article>
  )
}

export function BlogListClient({
  categoryIds,
  hasNextPage: initialHasNextPage,
  initialPosts,
  limit,
  loadingMessage,
  nextPage: initialNextPage,
  showDescription,
}: BlogListClientProps) {
  const loadingText = loadingMessage?.trim() || 'Loading more posts...'
  const [posts, setPosts] = useState(initialPosts)
  const [hasNextPage, setHasNextPage] = useState(initialHasNextPage)
  const [nextPage, setNextPage] = useState(initialNextPage ?? null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const categoryKey = categoryIds.join(',')

  const loadNextPage = useCallback(async () => {
    if (!hasNextPage || !nextPage || isLoading) return

    setIsLoading(true)
    setError(null)

    try {
      const params = new URLSearchParams({
        categoryIds: categoryKey,
        limit: String(limit),
        page: String(nextPage),
        showDescription: showDescription ? 'true' : 'false',
      })
      const response = await fetch(`/api/blog-posts?${params.toString()}`)
      const data = (await response.json()) as BlogListResponse & { error?: string }

      if (!response.ok) {
        setError(data.error || 'Could not load more blog posts.')
        return
      }

      setPosts((currentPosts) => {
        const seenIds = new Set(currentPosts.map((post) => post.id))
        const nextPosts = (data.posts || []).filter((post) => !seenIds.has(post.id))
        return [...currentPosts, ...nextPosts]
      })
      setHasNextPage(Boolean(data.hasNextPage))
      setNextPage(data.nextPage ?? null)
    } catch {
      setError('Could not load more blog posts.')
    } finally {
      setIsLoading(false)
    }
  }, [categoryKey, hasNextPage, isLoading, limit, nextPage, showDescription])

  useEffect(() => {
    const target = loadMoreRef.current
    if (!target || !hasNextPage || isLoading || error) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void loadNextPage()
        }
      },
      { rootMargin: '600px 0px' },
    )

    observer.observe(target)
    return () => observer.disconnect()
  }, [error, hasNextPage, isLoading, loadNextPage])

  if (!posts.length) return null

  return (
    <section className="flex w-full flex-col gap-theme-md md:gap-theme-lg">
      <div className="flex flex-col gap-theme-md md:gap-theme-lg">
        {posts.map((post) => (
          <BlogListCard key={post.id} post={post} showDescription={showDescription} />
        ))}
      </div>
      {hasNextPage && (
        <div ref={loadMoreRef} className="flex min-h-10 items-center justify-center">
          {error ? (
            <button type="button" className="theme-card-content underline" onClick={() => void loadNextPage()}>
              {error} Try again.
            </button>
          ) : (
            <p className="theme-card-content" aria-live="polite">
              {isLoading ? loadingText : 'Scroll to load more posts.'}
            </p>
          )}
        </div>
      )}
    </section>
  )
}
